import { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { logActivity, getLatestActivity } from "../api/api";

export const STATUS_OPTIONS = [
  { value: "active",  label: "Active",  color: "#22c55e" },
  { value: "break",   label: "Break",   color: "#f59e0b" },
  { value: "remote",  label: "Remote",  color: "#06b6d4" },
  { value: "dayoff",  label: "Day Off", color: "#8b5cf6" },
  { value: "offline", label: "Offline", color: "#6b7280" },
];

const StatusContext = createContext();

// ✅ بنستخدمه بس عشان منعملش نداء للسيرفر أكتر من مرة لنفس اليوزر
// في نفس التاب/الجلسة (مش عشان نجبر offline زي الأول).
const SESSION_MARKER_KEY = "status-session-marker";

function normalizeStatusValue(raw) {
  const s = String(raw ?? "").toLowerCase().trim();
  if (!s) return "offline";
  if (["day off", "day-off", "day_off", "dayoff"].includes(s)) return "dayoff";
  if (s.includes("break")) return "break";
  if (["off", "offline"].includes(s)) return "offline";
  if (["remote", "wfh"].includes(s)) return "remote";
  if (["active", "online", "working"].includes(s)) return "active";
  return STATUS_OPTIONS.some((o) => o.value === s) ? s : "offline";
}

function showStatusToast(status) {
  const statusMeta = STATUS_OPTIONS.find((s) => s.value === status);
  const color = statusMeta?.color ?? "#6b7280";
  const label = statusMeta?.label ?? status;

  toast(`Status: ${label}`, {
    icon: (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
    ),
    style: {
      background: `${color}96`,
      border: `1px solid ${color}66`,
      color: "#fff",
    },
  });
}

export function StatusProvider({ children }) {

  const [statuses, setStatuses] = useState(() => {
    try {
      const saved = localStorage.getItem("user-statuses");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem("user-statuses", JSON.stringify(statuses));
  }, [statuses]);

  // ✅ FIX: بدل ما نجبر offline على كل جلسة جديدة، بنسحب آخر حالة
  // حقيقية لليوزر من السيرفر (getLatestActivity) ونعرضها زي ما هي.
  // ده اللي بيخلي الحالة:
  //  1) تفضل ثابتة حتى لو قفلت التطبيق وفتحته تاني — لأنها بتتقرا
  //     من السيرفر مش من localStorage المحلي.
  //  2) تتزامن بين الأجهزة — لو انت active من اللاب توب وفتحت من
  //     الموبايل، الموبايل هيسأل السيرفر ويلاقي "active" فيرجّعها
  //     زي ما هي، مش offline.
  //  3) offline بيبقى بس في حالة واحدة: لما السيرفر يرجّع إنه مفيش
  //     أي نشاط اتسجل خالص لليوزر ده من الأساس (أول استخدام فعلي).
  useEffect(() => {
    const syncStatusFromServer = async () => {
      try {
        const stored = localStorage.getItem("user");
        if (!stored) return;
        const user = JSON.parse(stored);
        const userId = user?._id || user?.id;
        if (!userId) return;

        const marker = sessionStorage.getItem(SESSION_MARKER_KEY);
        if (marker === String(userId)) return; // اتسحبت الحالة قبل كده في نفس التاب
        sessionStorage.setItem(SESSION_MARKER_KEY, String(userId));

        let latestStatus = null;
        try {
          const res = await getLatestActivity();
          latestStatus =
            res?.data?.status ??
            res?.status ??
            (Array.isArray(res?.data) ? res.data[0]?.status : null) ??
            null;
        } catch (err) {
          // 404 أو أي خطأ = معتبرينه "مفيش نشاط سابق" لليوزر ده
          latestStatus = null;
        }

        if (latestStatus) {
          setStatuses((prev) => ({
            ...prev,
            [userId]: normalizeStatusValue(latestStatus),
          }));
        } else {
          // أول استخدام حقيقي لليوزر ده — هنا بس نفرض offline
          setStatuses((prev) => ({ ...prev, [userId]: "offline" }));
          logActivity("offline").catch((err) =>
            console.warn("Failed to log initial offline status:", err)
          );
        }
      } catch (err) {
        console.warn("syncStatusFromServer error:", err);
      }
    };

    syncStatusFromServer();

    window.addEventListener("userChanged", syncStatusFromServer);
    return () => window.removeEventListener("userChanged", syncStatusFromServer);
  }, []);

  const setUserStatus = async (userId, status, userInfo = {}) => {
    if (!userId) return;

    const prevStatus = statuses[userId] || "offline";
    if (prevStatus === status) return;

    try {
      await logActivity(status);
      setStatuses(prev => ({ ...prev, [userId]: status }));

      showStatusToast(status);

      window.dispatchEvent(new CustomEvent("userStatusChanged", {
        detail: { userId, status, timestamp: new Date() },
      }));
    } catch (err) {
      console.error("Status update failed:", err);
      toast.error("Failed to update status");
    }
  };

  const getUserStatus = (userId) => {
    if (!userId) return "offline";
    return statuses[userId] || "offline";
  };

  return (
    <StatusContext.Provider value={{ getUserStatus, setUserStatus, STATUS_OPTIONS }}>
      {children}
    </StatusContext.Provider>
  );
}

export const useStatus = () => useContext(StatusContext);